import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, Star, ChevronRight, Send, Loader2, Film, Users, Award, BookOpen, Clock, MapPin } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const TrainingPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: 'Training Enquiry',
        message: form.message
      });
      setSent(true);
      toast.success('Enquiry sent! We\'ll be in touch shortly.');
    } catch (error) {
      toast.error('Failed to send. Please email info@2ndteam.co.uk directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-28 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
            <Link to="/register?role=standin" className="text-white/70 hover:text-white transition-colors">Apply</Link>
            <Link to="/login" className="text-white/70 hover:text-white transition-colors">Login</Link>
          </div>
          <a href="#enquire">
            <Button className="gold-gradient text-black font-semibold hover:brightness-110 transition-all" data-testid="nav-enquire-btn">
              Book Training
            </Button>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <img 
          src="https://customer-assets.emergentagent.com/job_eb07d9f1-df0a-45f8-8183-e35769881cf0/artifacts/35w6msz2_IMG20260228095435.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.15) saturate(0.6)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#050505]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm">
            <Award className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-white/70">UK's Only Industry-Endorsed Training</span>
          </div>

          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="text-white">London Based</span><br />
            <span className="gold-text">Stand-In Training</span>
          </h1>

          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
            2nd Team is the sole provider of professional training for Stand-ins and Picture Doubles in the UK. We give you the experience and expertise to work on the biggest productions from Disney to DC.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#enquire">
              <Button className="gold-gradient text-black font-bold text-lg px-8 py-6 uppercase tracking-wider hover:brightness-110 transition-all" data-testid="hero-enquire-btn">
                Book a Training Day
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Course Overview */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold mb-4">
              <span className="gold-text">One Day Course</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Everything you need to become set-ready and start working on UK film and TV productions
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mb-16">
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-white font-semibold mb-1">Full Day</p>
              <p className="text-white/50 text-sm">Comprehensive one-day intensive</p>
            </div>
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-white font-semibold mb-1">London</p>
              <p className="text-white/50 text-sm">Paddington area</p>
            </div>
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Star className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-white font-semibold mb-1">£175</p>
              <p className="text-white/50 text-sm">Investment in your career</p>
            </div>
          </div>

          {/* What You'll Learn */}
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-6">What You'll Learn</h3>
              <div className="space-y-4">
                {[
                  'On-set vocabulary and the language of film production',
                  'Procedures from call time arrival through to wrap',
                  'Working with 1st ADs, Crowd 2nds and camera departments',
                  'Hitting marks, eye-lines and continuity basics',
                  'Professional etiquette and set behaviour',
                  'How to read and understand a call sheet'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-6">What You'll Get</h3>
              <div className="space-y-4">
                {[
                  'Qualification to stand-in on any platform as a registered stand-in',
                  'Access to the 2nd Team roster for consistent work opportunities',
                  'Industry contacts and networking with working professionals',
                  'Lowest agency fees in the industry — just 15% (ex VAT)',
                  'Ongoing support and guidance from 2nd Team',
                  'A pathway into other departments: VFX, ADs, Camera, Sound, SFX, Grips'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unlock Your Potential */}
      <section className="relative py-20 px-6 overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_eb07d9f1-df0a-45f8-8183-e35769881cf0/artifacts/o3o6zpxq_IMG20251215145654~2.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.15) saturate(0.5)' }}
        />
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-6">
            It's All About Who You Know
          </h2>
          <p className="text-white/60 text-lg leading-relaxed max-w-3xl mx-auto">
            Getting your start in the film industry is notoriously difficult. Many of those who trained with 2nd Team have progressed and gone on to work in other departments on set including VFX, ADs, Camera, Sound, SFX and Grips. Standing in gives you the chance to be there, meet the people of influence and learn which department might best suit you.
          </p>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
            ))}
          </div>
          <blockquote className="font-['Playfair_Display'] text-xl md:text-2xl text-white italic mb-6 leading-relaxed">
            "I wanted to get into the film industry so I invested in myself and did the Stand-in training with 2nd Team. I've been working solidly now for the last 6 months on my dream production. I never thought I could progress so quickly!"
          </blockquote>
          <div className="text-[#D4AF37] font-semibold">— Gabriella, Stand-In</div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquire" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold mb-4">
              <span className="gold-text">Book a Training Day</span>
            </h2>
            <p className="text-white/60">
              Fill out the form below and we'll be in touch to arrange your training.
            </p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="training-contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-white/80">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                    data-testid="training-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                    data-testid="training-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+44 7700 900000"
                  className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                  data-testid="training-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Message *</Label>
                <Textarea
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us a bit about yourself and why you'd like to train with 2nd Team..."
                  required
                  rows={5}
                  className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] resize-none"
                  data-testid="training-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full gold-gradient text-black font-bold h-12 uppercase tracking-wider hover:brightness-110 transition-all disabled:opacity-50"
                data-testid="training-submit-btn"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {sending ? 'Sending...' : 'Send Enquiry'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-4">Enquiry Sent</h3>
              <p className="text-white/60 mb-8">Thanks for your interest! We'll be in touch shortly to arrange your training day.</p>
              <Link to="/">
                <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37]">Back to Home</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
            alt="2nd Team Casting" 
            className="h-20 w-auto"
          />
          <div className="flex items-center gap-6 text-white/40 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <a href="mailto:info@2ndteam.co.uk" className="hover:text-white transition-colors">info@2ndteam.co.uk</a>
          </div>
          <div className="text-white/30 text-sm">© 2026 2nd Team Casting</div>
        </div>
      </footer>
    </div>
  );
};

export default TrainingPage;
